import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { PaginationWithLinks } from './Pagination'
import DataTableEnumType from '@/Enums/DataTableTypeEnum'
import Constent from '@/utils/constent'
import { format } from 'date-fns'
import { Checkbox } from '../ui/checkbox'
import Sortable from './Sortable'
import { ChevronDown, ChevronUp } from 'lucide-react'
import NoDataFound from './NoDataFound'
import Loader from './Loader'
import StatusBadge from './StatusBadge'

const Datatable = ({ columns, data, totalDataCount, type, allcheck, updateEvent, deleteId, loading = false }) => {
    const location = useLocation()
    const navigate = useNavigate()
    const searchParams = new URLSearchParams(location.search)
    const pageSize = Constent.PAGINATION_SIZE
    const currentPage = parseInt(searchParams.get('page') || 1)
    const tbodyRef = useRef(null)
    const [isScrollable, setIsScrollable] = useState(false)
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        rowCount: pageSize,
    })

    useEffect(() => {
        const checkIfScrollable = () => {
            if (tbodyRef.current) {
                const isScrollable = tbodyRef.current.scrollHeight > tbodyRef.current.clientHeight
                setIsScrollable(isScrollable)
            }
        }
        checkIfScrollable()
        window.addEventListener('resize', checkIfScrollable)
        return () => {
            window.removeEventListener('resize', checkIfScrollable)
        }
    }, [])
    // const location = useLocation()
    const handleRedirect = (row) => {
        if (DataTableEnumType.HCHB_PAYOR_TYPE == type) {
            navigate(`/setup/hchb-payor-type/${row.hchb_payor_type}`)
        }
        if (DataTableEnumType.SECTION == type) {
            navigate(`/setup/section/${row.section_name}`)
        }
        if (DataTableEnumType.SHIFT_TYPE == type) {
            navigate(`/setup/shift-type/${row.name}`)
        }
        if (DataTableEnumType.MITEM == type) {
            navigate(`/setup/mitem/${row.mitem}`)
        }
        if (DataTableEnumType.USER == type) {
            navigate(`/user/${row.name}`)
        }
        if (DataTableEnumType.BULK_UPLOAD == type) {
            navigate(`/bulk-upload/${row.name}`)
        }
        if (DataTableEnumType.ACTIVITY_LOG == type) {
            navigate(`/activity-log/${row.name}`)
        }
        if (DataTableEnumType.CLIENT == type) {
            navigate(`/client/${row.name}`)
        }
        if (DataTableEnumType.GROUP_CONFIGURATION == type) {
            navigate(`/configuration/group-configuration/${row.name}`)
        }
        if(DataTableEnumType.WORKFLOW==type){
            navigate(`/workflow/${row.workflow_name}`)
        }
    }

    const handleCheckboxChange = (event, row) => {
        try {
            if (event) {
                let deleteIdArray
                if (!deleteId?.includes(row.name)) {
                    deleteIdArray = [...deleteId, row.name]
                    updateEvent({ deleteId: deleteIdArray })
                }
                if (deleteIdArray.length === data.length) {
                    updateEvent({ allcheck: true })
                } else {
                    updateEvent({ allcheck: false })
                }
            } else {
                updateEvent({ allcheck: false })
                if (deleteId?.includes(row.name)) {
                    let newdeleteId = deleteId.filter((item) => item !== row.name)
                    updateEvent({ deleteId: newdeleteId })
                }
            }
        } catch (err) {
            console.log(err)
        }
    }

    const handleCheckboxClick = (event) => {
        event.stopPropagation()
    }

    const handleAllChange = (event, rows) => {
        updateEvent({ allcheck: event })

        if (event) {
            rows.map((row, index) => {
                if (!deleteId.includes(row.original.name)) {
                    deleteId.push(row.original.name)
                    console.log('test all ', deleteId)
                    updateEvent({ deleteId: deleteId })
                }
            })
        } else {
            updateEvent({ deleteId: [] })
        }
    }

    return (
        <div className="gap-y-[1.75rem] border rounded-md pb-2">
            <div
                className=" overflow-auto overflow-card-scroll"
                style={{
                    maxHeight: 'calc(100vh - 267px)',
                    marginBottom: '2px',
                }}
            >
                <table className="w-full">
                    <thead className="sticky top-0 z-20 bg-gray-100 border-b">
                        <tr className="">
                            {table.getHeaderGroups().map((headerGroup) =>
                                headerGroup.headers.map((header) => {
                                    return (
                                        <th
                                            key={'datatableHeadData' + header.id}
                                            className={`px-2 py-1 text-[13px] font-bold ${header.id === 'checkbox' ? ' w-5 ' : ' flex-1 '}`}
                                        >
                                            {header.id === 'checkbox' ? (
                                                <Checkbox
                                                    className="mx-3 my-1"
                                                    checked={allcheck}
                                                    onCheckedChange={(event) => handleAllChange(event, table.getRowModel().rows)}
                                                />
                                            ) : (
                                                <span className="flex items-center">
                                                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                                    <span className="ml-16">
                                                        {header.column.columnDef?.sort && (
                                                            <Sortable accessorKey={header.column.columnDef.accessorKey} />
                                                        )}
                                                    </span>
                                                </span>
                                            )}
                                        </th>
                                    )
                                })
                            )}
                        </tr>
                    </thead>
                    <tbody className="">
                        {table.getRowModel().rows?.length === 0 ? (
                            <tr>
                                <td colSpan="100%" className="h-20">
                                    {loading ? (
                                        <div className="flex justify-center items-center w-full" style={{ zIndex: 99999 }}>
                                            <div className="loader"></div>
                                        </div>
                                    ) : (
                                        <NoDataFound />
                                    )}
                                </td>
                            </tr>
                        ) : table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row, pI) => (
                                <tr
                                    key={'datatableBodyRow' + pI}
                                    className={`border-b cursor-pointer hover:bg-gray-50 ${(pI + 1) % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                                    onClick={() => handleRedirect(row.original)}
                                >
                                    {row.getVisibleCells().map((cell, i) => {
                                        return (
                                            <td
                                                key={'datatableBodyData' + i}
                                                className={`px-2 py-1 text-[13px] ${cell.column.id === 'checkbox' ? 'w-5' : 'flex-1'}`}
                                            >
                                                {cell.column.id === 'checkbox' ? (
                                                    allcheck ? (
                                                        <Checkbox
                                                            className="mx-3 my-1"
                                                            onCheckedChange={(event) => handleCheckboxChange(event, row.original)}
                                                            checked={allcheck}
                                                        />
                                                    ) : (
                                                        <Checkbox
                                                            className="mx-3 my-1"
                                                            onClick={(event) => handleCheckboxClick(event)}
                                                            onCheckedChange={(event) => handleCheckboxChange(event, row.original)}
                                                            checked={deleteId?.includes(row.original.name)}
                                                        />
                                                    )
                                                ) : cell.column.id === 'status' ? (
                                                    <>{<StatusBadge status={cell.column.id == 'status' ? row.getValue('status') : row.getValue('enabled')} />}</>
                                                ) : cell.column.id === 'date' || cell.column.id === 'created_at' ? (
                                                    <>{format(flexRender(cell.column.columnDef.cell, cell.getContext()), Constent.DATE_FORMAT)}</>
                                                ) : (
                                                    <>{flexRender(cell.column.columnDef.cell, cell.getContext())}</>
                                                )}
                                            </td>
                                        )
                                    })}
                                </tr>
                            ))
                        ) : null}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-end items-end mx-3">
                <PaginationWithLinks totalCount={totalDataCount} />
            </div>
        </div>
    )
}

export default Datatable